import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Loader2, Users, Sparkles, CheckCircle2, Archive, Pencil,
  Building2, ScrollText, AlertTriangle, XCircle, Plus, Undo2, RotateCcw,
} from "lucide-react";

const CLS = {
  speaking: { label: "Speaking", c: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30" },
  present_non_speaking: { label: "Present · non-speaking", c: "bg-sky-500/15 text-sky-300 ring-sky-500/30" },
  mentioned_offstage: { label: "Mentioned · offstage", c: "bg-slate-600/25 text-slate-300 ring-slate-500/30" },
  unidentified_person_reference: { label: "Unidentified person reference", c: "bg-fuchsia-500/15 text-fuchsia-300 ring-fuchsia-500/30" },
  unresolved: { label: "Unresolved", c: "bg-amber-500/15 text-amber-300 ring-amber-500/30" },
};
const CLS_OPTIONS = ["speaking", "present_non_speaking", "mentioned_offstage", "unidentified_person_reference", "unresolved"];

function Pill({ cls }) {
  const m = CLS[cls] || CLS.unresolved;
  return <span className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase tracking-wider ring-1 ${m.c}`}>{m.label}</span>;
}
function Conf({ v }) {
  const pct = Math.round((v || 0) * 100);
  const color = pct >= 85 ? "text-emerald-400" : pct >= 60 ? "text-amber-400" : "text-red-400";
  return <span className={`font-mono text-xs ${color}`}>{pct}%</span>;
}
function ConfStat({ label, value, tone, hint }) {
  const c = { emerald: "text-emerald-300", amber: "text-amber-300", red: "text-red-300", fuchsia: "text-fuchsia-300" }[tone];
  return (
    <div className="rounded-lg border border-border bg-[#12161f] px-4 py-3" data-testid={`conf-${label.toLowerCase()}`}>
      <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label} <span className="text-slate-600">{hint}</span></div>
      <div className={`mt-1 font-mono text-2xl ${c}`}>{value}</div>
    </div>
  );
}

export function CharacterStudio() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [chars, setChars] = useState([]);
  const [entities, setEntities] = useState([]);
  const [attributions, setAttributions] = useState({ total: 0, items: [] });
  const [benchmark, setBenchmark] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState("");
  const [editChar, setEditChar] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [resolveChar, setResolveChar] = useState(null);
  const [stats, setStats] = useState(null);
  const [passages, setPassages] = useState([]);
  const [activePassage, setActivePassage] = useState(null);
  const poll = useRef(null);

  const loadAll = useCallback(async () => {
    const [c, e, a, b, r, s, p] = await Promise.all([
      api.get(`/projects/${projectId}/characters`).then((x) => x.data).catch(() => []),
      api.get(`/projects/${projectId}/entities`).then((x) => x.data).catch(() => []),
      api.get(`/projects/${projectId}/attributions?limit=300`).then((x) => x.data).catch(() => ({ total: 0, items: [] })),
      api.get(`/projects/${projectId}/benchmark-comparison`).then((x) => x.data).catch(() => null),
      api.get(`/projects/${projectId}/analysis-runs`).then((x) => x.data).catch(() => []),
      api.get(`/projects/${projectId}/attribution-stats`).then((x) => x.data).catch(() => null),
      api.get(`/benchmark-passages`).then((x) => x.data).catch(() => []),
    ]);
    setChars(c); setEntities(e); setAttributions(a); setBenchmark(b); setRuns(r); setStats(s); setPassages(p);
  }, [projectId]);

  const goToEvidence = (order) => { if (order != null) navigate(`/studio/projects/${projectId}/manuscript?focus=${order}`); };

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
    loadAll().finally(() => setLoading(false));
    return () => poll.current && clearInterval(poll.current);
  }, [projectId, loadAll]);

  const runDetection = async (force = false) => {
    if (force && !window.confirm("Force a full re-analysis? This calls Claude again (~5 min) and refreshes evidence. Approved and edited records are preserved.")) return;
    setRunning(true);
    setRunStatus(force ? "Forcing re-analysis with Claude Sonnet 4.6…" : "Checking detection…");
    try {
      const { data } = await api.post(`/projects/${projectId}/analyze${force ? "?force=true" : ""}`);
      if (data.status === "cached") {
        setRunning(false);
        setRunStatus("");
        toast.success(`Detection is up to date — ${data.characters} characters, ${data.entities} entities, ${data.attributions} attributions (unchanged).`);
        await loadAll();
        return;
      }
      setRunStatus("Analyzing characters & dialogue… (this can take a few minutes)");
      const runId = data.run_id;
      poll.current = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/analysis-runs/${runId}`);
          if (s.status === "completed") {
            clearInterval(poll.current);
            setRunning(false);
            setRunStatus("");
            toast.success(`Detection complete: ${s.character_count} characters, ${s.entity_count} entities, ${s.attribution_count} attributions.`);
            await loadAll();
          } else if (s.status === "failed") {
            clearInterval(poll.current);
            setRunning(false);
            setRunStatus("");
            toast.error(`Detection failed: ${s.error || "unknown error"}`);
            await loadAll();
          }
        } catch {
          /* keep polling */
        }
      }, 4000);
    } catch (e) {
      setRunning(false);
      setRunStatus("");
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const approve = async (id) => {
    await api.post(`/characters/${id}/approve`);
    toast.success("Character approved.");
    loadAll();
  };
  const unapprove = async (id) => {
    await api.post(`/characters/${id}/unapprove`);
    toast.success("Returned to draft.");
    loadAll();
  };
  const archive = async (id) => {
    await api.post(`/characters/${id}/archive`);
    toast.success("Character archived.");
    loadAll();
  };

  const renderEvidenceChips = (items) =>
    items?.length ? (
      <div className="mt-3 flex flex-wrap gap-1">
        {items.slice(0, 8).map((ev, i) => (
          <button key={i} data-testid={`evidence-link-${ev.order}`} onClick={() => goToEvidence(ev.order)} title={ev.snippet}
            className="rounded bg-slate-800/60 px-1.5 py-0.5 font-mono text-[10px] text-amber-300 ring-1 ring-border transition-colors hover:bg-amber-500/15">
            ¶{ev.body_number}
          </button>
        ))}
      </div>
    ) : null;

  const renderCard = (c, isRef) => (
    <div key={c.id} data-testid={`character-card-${c.id}`} className="rounded-xl border border-border bg-[#12161f] p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-serif text-xl text-slate-100">{c.name}</h3>
            {c.is_viewpoint && <Badge className="border-0 bg-amber-500/15 text-[9px] uppercase tracking-wider text-amber-300">Viewpoint</Badge>}
            {c.status === "approved" && <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
          </div>
          {c.aliases?.length > 0 && <p className="mt-0.5 font-mono text-[11px] text-slate-500">aka {c.aliases.join(", ")}</p>}
        </div>
        <div className="text-right">
          <Conf v={c.confidence} />
          <div className="mt-1 font-mono text-[10px] text-slate-500">{c.speaking_line_count} lines · {c.evidence?.length || 0} refs</div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2"><Pill cls={c.classification} />
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-600">{c.status}</span>
        {c.edited && <span className="rounded-full bg-sky-500/15 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-sky-300 ring-1 ring-sky-500/30">edited</span>}
      </div>
      {c.literary_profile?.summary && <p className="mt-3 line-clamp-2 text-sm text-slate-400">{c.literary_profile.summary}</p>}
      {renderEvidenceChips(c.evidence)}
      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" variant="outline" data-testid={`edit-character-${c.id}`} onClick={() => setEditChar(c)}
          className="h-8 gap-1 border-border text-slate-300 hover:bg-slate-800/50"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
        {isRef ? (
          <Button size="sm" data-testid={`resolve-character-${c.id}`} onClick={() => setResolveChar(c)}
            className="h-8 gap-1 bg-fuchsia-600 text-white hover:bg-fuchsia-500"><Sparkles className="h-3.5 w-3.5" /> Resolve</Button>
        ) : c.status === "approved" ? (
          <Button size="sm" variant="outline" data-testid={`unapprove-character-${c.id}`} onClick={() => unapprove(c.id)}
            className="h-8 gap-1 border-amber-500/40 text-amber-200 hover:bg-amber-500/10"><Undo2 className="h-3.5 w-3.5" /> Return to draft</Button>
        ) : (
          <Button size="sm" data-testid={`approve-character-${c.id}`} onClick={() => approve(c.id)}
            className="h-8 gap-1 bg-emerald-600 text-white hover:bg-emerald-500"><CheckCircle2 className="h-3.5 w-3.5" /> Approve</Button>
        )}
        <Button size="sm" variant="ghost" data-testid={`archive-character-${c.id}`} onClick={() => archive(c.id)}
          className="h-8 gap-1 text-slate-500 hover:bg-red-500/10 hover:text-red-300"><Archive className="h-3.5 w-3.5" /></Button>
      </div>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>;
  }

  const hasRoster = chars.length > 0;

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate(`/studio/projects/${projectId}`)} data-testid="back-to-project"
        className="mb-4 gap-2 text-slate-400 hover:text-amber-200">
        <ArrowLeft className="h-4 w-4" /> {project?.title || "Project"}
      </Button>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Phase 3 · Character Studio</div>
          <h1 className="font-serif text-4xl tracking-tight text-slate-100">Character Studio</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-400">
            AI-proposed roster, classifications, evidence and dialogue attribution — every record is a
            <span className="text-amber-300"> draft awaiting your approval</span>. Detection runs independently;
            the benchmark is only a scoring reference.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasRoster && (
            <Button variant="outline" data-testid="force-detection-button" onClick={() => runDetection(true)} disabled={running}
              className="gap-2 border-border text-slate-300 hover:bg-slate-800/50">
              <RotateCcw className="h-4 w-4" /> Force re-analyze
            </Button>
          )}
          <Button data-testid="run-detection-button" onClick={() => runDetection(false)} disabled={running}
            className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {hasRoster ? "Re-run detection" : "Run detection"}
          </Button>
        </div>
      </div>

      {running && (
        <div data-testid="detection-status" className="mb-6 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-200">
          <Loader2 className="h-4 w-4 animate-spin" /> {runStatus}
        </div>
      )}

      {!hasRoster && !running ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-[#12161f]/40 py-24 text-center">
          <Users className="mb-3 h-8 w-8 text-amber-400" />
          <h2 className="font-serif text-2xl text-slate-200">No roster yet</h2>
          <p className="mt-2 max-w-sm text-sm text-slate-500">Run detection to propose characters, entities and dialogue attributions from the imported manuscript.</p>
        </div>
      ) : (
        <Tabs defaultValue="roster">
          <TabsList className="bg-[#12161f]">
            <TabsTrigger value="roster" data-testid="tab-roster">Roster ({chars.length})</TabsTrigger>
            <TabsTrigger value="entities" data-testid="tab-entities">Entities ({entities.length})</TabsTrigger>
            <TabsTrigger value="dialogue" data-testid="tab-dialogue">Dialogue ({attributions.total})</TabsTrigger>
            <TabsTrigger value="benchmark" data-testid="tab-benchmark">Benchmark</TabsTrigger>
            <TabsTrigger value="runs" data-testid="tab-runs">Runs ({runs.length})</TabsTrigger>
          </TabsList>

          {/* ROSTER */}
          <TabsContent value="roster" className="mt-5">
            <div className="mb-3 flex justify-end">
              <Button size="sm" variant="outline" data-testid="add-character-button" onClick={() => setAddOpen(true)}
                className="gap-1 border-border text-slate-300 hover:bg-slate-800/50">
                <Plus className="h-3.5 w-3.5" /> Add character
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {chars.filter((c) => c.classification !== "unidentified_person_reference").map((c) => renderCard(c, false))}
            </div>

            {chars.some((c) => c.classification === "unidentified_person_reference") && (
              <div className="mt-8">
                <div className="mb-3 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-fuchsia-300/80">
                  <Users className="h-3.5 w-3.5" /> Unidentified person references
                </div>
                <p className="mb-3 max-w-2xl text-xs text-slate-500">
                  People referred to only by relationship/role, never named in the chapter. These are NOT permanent
                  characters — resolve one into an existing or new character later; its evidence and dialogue history carry over.
                </p>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="unidentified-references">
                  {chars.filter((c) => c.classification === "unidentified_person_reference").map((c) => renderCard(c, true))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ENTITIES */}
          <TabsContent value="entities" className="mt-5">
            <div className="rounded-xl border border-border bg-[#12161f]">
              {entities.map((e) => (
                <div key={e.id} data-testid={`entity-row-${e.id}`} className="border-b border-border px-4 py-3 last:border-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      <div>
                        <div className="text-sm text-slate-200">{e.name}</div>
                        {e.note && <div className="font-mono text-[11px] text-slate-500">{e.note}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Conf v={e.confidence} />
                      <Select value={e.type} onValueChange={async (val) => { await api.patch(`/entities/${e.id}`, { type: val }); loadAll(); }}>
                        <SelectTrigger data-testid={`entity-type-${e.id}`} className="h-8 w-44 bg-slate-950/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="border-border bg-[#12161f] text-slate-100">
                          {["venue", "organization", "location", "title", "relationship_reference", "background_role", "unknown"].map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {renderEvidenceChips(e.evidence)}
                </div>
              ))}
            </div>
          </TabsContent>

          {/* DIALOGUE */}
          <TabsContent value="dialogue" className="mt-5">
            {stats && (
              <div className="mb-4 grid grid-cols-4 gap-2" data-testid="confidence-totals">
                <ConfStat label="High" value={stats.high} tone="emerald" hint="≥85%" />
                <ConfStat label="Medium" value={stats.medium} tone="amber" hint="60–84%" />
                <ConfStat label="Low" value={stats.low} tone="red" hint="<60%" />
                <ConfStat label="Unresolved" value={stats.unresolved} tone="fuchsia" hint="no speaker" />
              </div>
            )}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Review sets:</span>
              <button data-testid="passage-all" onClick={() => setActivePassage(null)}
                className={`rounded-full px-3 py-1 text-xs ring-1 ${activePassage === null ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border hover:bg-slate-800/50"}`}>
                All ({attributions.total})
              </button>
              {passages.map((p) => (
                <button key={p.key} data-testid={`passage-${p.key}`} onClick={() => setActivePassage(p)} title={p.description}
                  className={`rounded-full px-3 py-1 text-xs ring-1 ${activePassage?.key === p.key ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border hover:bg-slate-800/50"}`}>
                  {p.name} · ¶{p.from}–{p.to}
                </button>
              ))}
            </div>
            {activePassage && (
              <div className="mb-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.05] px-4 py-2 text-xs text-amber-200/90">
                {activePassage.description} · Expected: {activePassage.expected_speakers.join(", ")}
              </div>
            )}
            <div className="space-y-2">
              {attributions.items
                .filter((a) => !activePassage || (a.paragraph_number >= activePassage.from && a.paragraph_number <= activePassage.to))
                .map((a) => (
                  <div key={a.id} data-testid={`attribution-${a.id}`} className="flex items-start justify-between gap-4 rounded-lg border border-border bg-[#12161f] px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <button data-testid={`attribution-jump-${a.id}`} onClick={() => goToEvidence(a.paragraph_order)}
                          className="rounded bg-slate-800/60 px-1.5 py-0.5 font-mono text-[10px] text-amber-300 ring-1 ring-border hover:bg-amber-500/15">¶{a.paragraph_number}</button>
                        {a.status === "unresolved" && <Badge className="border-0 bg-fuchsia-500/15 text-[9px] uppercase tracking-wider text-fuchsia-300">Unresolved</Badge>}
                        {a.status === "approved" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
                        <Conf v={a.confidence} />
                      </div>
                      <p className="mt-1 truncate font-serif text-sm text-slate-300">“{a.quote}”</p>
                      {a.reason && <p className="font-mono text-[10px] text-slate-600">{a.reason}</p>}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Select value={a.speaker_character_id || "unresolved"}
                        onValueChange={async (val) => {
                          if (val === "unresolved") await api.patch(`/attributions/${a.id}`, { speaker_character_id: "", speaker_name: "Unresolved", status: "unresolved" });
                          else await api.patch(`/attributions/${a.id}`, { speaker_character_id: val, status: "draft" });
                          loadAll();
                        }}>
                        <SelectTrigger data-testid={`attribution-speaker-${a.id}`} className="h-8 w-40 bg-slate-950/60 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="border-border bg-[#12161f] text-slate-100">
                          <SelectItem value="unresolved">Unresolved</SelectItem>
                          {chars.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button size="sm" data-testid={`approve-attribution-${a.id}`} disabled={a.status === "unresolved"}
                        onClick={async () => { await api.patch(`/attributions/${a.id}`, { status: "approved" }); loadAll(); }}
                        className="h-8 bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40">✓</Button>
                    </div>
                  </div>
                ))}
            </div>
          </TabsContent>

          {/* BENCHMARK */}
          <TabsContent value="benchmark" className="mt-5">
            {benchmark && (
              <div className="space-y-5">
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border border-border bg-[#12161f] px-4 py-3">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Character match rate</div>
                    <div className="mt-1 font-mono text-2xl text-emerald-300">{Math.round(benchmark.score.character_match_rate * 100)}%</div>
                    <div className="font-mono text-[10px] text-slate-500">{benchmark.characters.matched}/{benchmark.characters.expected} expected</div>
                  </div>
                  <div className="rounded-lg border border-border bg-[#12161f] px-4 py-3">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Classification conflicts</div>
                    <div className="mt-1 font-mono text-2xl text-slate-100">{benchmark.score.classification_conflicts}</div>
                  </div>
                  <div className="rounded-lg border border-border bg-[#12161f] px-4 py-3">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Omissions</div>
                    <div className="mt-1 font-mono text-2xl text-slate-100">{benchmark.score.omissions}</div>
                  </div>
                </div>
                <ReportList title="Classification differences" icon={AlertTriangle} rows={benchmark.characters.classification_differences.map((d) => `${d.name}: expected ${d.expected}, detected ${d.detected}`)} tone="amber" />
                <ReportList title="Omissions (expected but not detected)" icon={XCircle} rows={benchmark.characters.omissions.map((o) => o.name)} tone="red" />
                <ReportList title="Additions (detected beyond benchmark)" icon={Plus} rows={benchmark.characters.additions.map((a) => `${a.name} (${a.classification})`)} tone="slate" />
                <ReportList title="Entity type differences" icon={AlertTriangle} rows={benchmark.entities.type_differences.map((d) => `${d.name}: expected ${d.expected}, detected ${d.detected}`)} tone="amber" />
                <ReportList title="Entity omissions" icon={XCircle} rows={benchmark.entities.omissions.map((o) => `${o.name} (${o.type})`)} tone="red" />
              </div>
            )}
          </TabsContent>

          {/* RUNS */}
          <TabsContent value="runs" className="mt-5">
            <div className="rounded-xl border border-border bg-[#12161f]">
              {runs.map((r) => (
                <div key={r.id} data-testid={`run-row-${r.id}`} className="border-b border-border px-4 py-3 last:border-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 font-mono text-xs text-slate-300">
                      <ScrollText className="h-3.5 w-3.5 text-amber-400" /> {r.model}
                      <span className="text-slate-600">·</span> <span className="text-slate-500">{r.prompt_version}</span>
                    </div>
                    <Badge className={`border-0 text-[9px] uppercase tracking-wider ${r.status === "completed" ? "bg-emerald-500/15 text-emerald-300" : r.status === "failed" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>{r.status}</Badge>
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-slate-500">
                    {r.started_at ? new Date(r.started_at).toLocaleString() : ""} · {r.duration_ms ? `${(r.duration_ms / 1000).toFixed(0)}s` : "—"} ·
                    {" "}{r.character_count}c / {r.entity_count}e / {r.attribution_count}a
                    {r.salvaged ? " · salvaged" : ""}
                  </div>
                  {r.error && <div className="mt-1 text-[11px] text-red-400">{r.error}</div>}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      )}

      {editChar && <EditCharacterDialog character={editChar} onClose={() => setEditChar(null)} onSaved={() => { setEditChar(null); loadAll(); }} />}
      {addOpen && <AddCharacterDialog projectId={projectId} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); loadAll(); }} />}
      {resolveChar && <ResolveDialog reference={resolveChar} characters={chars.filter((c) => c.classification !== "unidentified_person_reference")} onClose={() => setResolveChar(null)} onSaved={() => { setResolveChar(null); loadAll(); }} />}
    </div>
  );
}

function ReportList({ title, icon: Icon, rows, tone }) {
  const toneC = { amber: "text-amber-300", red: "text-red-300", slate: "text-slate-300" }[tone] || "text-slate-300";
  return (
    <div className="rounded-lg border border-border bg-[#12161f] p-4">
      <div className="mb-2 flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-slate-400"><Icon className={`h-3.5 w-3.5 ${toneC}`} /> {title}</div>
      {rows.length === 0 ? <div className="text-sm text-slate-600">None</div> :
        <ul className="space-y-1">{rows.map((r, i) => <li key={i} className={`text-sm ${toneC}`}>{r}</li>)}</ul>}
    </div>
  );
}

function EditCharacterDialog({ character, onClose, onSaved }) {
  const [name, setName] = useState(character.name);
  const [aliases, setAliases] = useState((character.aliases || []).join(", "));
  const [classification, setClassification] = useState(character.classification);
  const [lit, setLit] = useState(character.literary_profile?.summary || "");
  const [voc, setVoc] = useState(character.vocal_profile?.notes || "");
  const [notes, setNotes] = useState(character.notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/characters/${character.id}`, {
        name: name.trim(),
        aliases: aliases.split(",").map((a) => a.trim()).filter(Boolean),
        classification,
        literary_profile: { ...character.literary_profile, summary: lit },
        vocal_profile: { ...character.vocal_profile, notes: voc },
        notes,
      });
      toast.success("Character updated.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-amber-500/15 bg-[#12161f] text-slate-100 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit character</DialogTitle>
          <DialogDescription className="text-slate-400">Draft profile — grounded in manuscript evidence. Casting stays with narrator Scott.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
            <Input data-testid="edit-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-950/60" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Classification</Label>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger data-testid="edit-classification" className="bg-slate-950/60"><SelectValue /></SelectTrigger>
              <SelectContent className="border-border bg-[#12161f] text-slate-100">
                {CLS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{CLS[o].label}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Aliases (comma-separated)</Label>
            <Input data-testid="edit-aliases" value={aliases} onChange={(e) => setAliases(e.target.value)} className="bg-slate-950/60" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Literary profile</Label>
            <Textarea data-testid="edit-literary" value={lit} onChange={(e) => setLit(e.target.value)} rows={3} className="bg-slate-950/60" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Vocal profile notes</Label>
            <Textarea data-testid="edit-vocal" value={voc} onChange={(e) => setVoc(e.target.value)} rows={2} className="bg-slate-950/60" /></div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Director notes</Label>
            <Textarea data-testid="edit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="bg-slate-950/60" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
          <Button data-testid="save-character-button" onClick={save} disabled={saving} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResolveDialog({ reference, characters, onClose, onSaved }) {
  const [mode, setMode] = useState("existing");
  const [targetId, setTargetId] = useState(characters[0]?.id || "");
  const [newName, setNewName] = useState(reference.name);
  const [newClass, setNewClass] = useState("speaking");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = mode === "existing" ? { target_character_id: targetId } : { new_name: newName.trim(), new_classification: newClass };
      await api.post(`/characters/${reference.id}/resolve`, body);
      toast.success("Reference resolved. Evidence & dialogue carried over.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-fuchsia-500/20 bg-[#12161f] text-slate-100 sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Resolve “{reference.name}”</DialogTitle>
          <DialogDescription className="text-slate-400">Turn this unidentified reference into a character. Its evidence and any dialogue history carry over.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <button data-testid="resolve-mode-existing" onClick={() => setMode("existing")} className={`flex-1 rounded-md px-3 py-2 text-sm ring-1 ${mode === "existing" ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border"}`}>Existing character</button>
            <button data-testid="resolve-mode-new" onClick={() => setMode("new")} className={`flex-1 rounded-md px-3 py-2 text-sm ring-1 ${mode === "new" ? "bg-amber-500/15 text-amber-200 ring-amber-500/30" : "text-slate-400 ring-border"}`}>New character</button>
          </div>
          {mode === "existing" ? (
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger data-testid="resolve-target-select" className="bg-slate-950/60"><SelectValue placeholder="Choose character" /></SelectTrigger>
              <SelectContent className="border-border bg-[#12161f] text-slate-100">
                {characters.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">New character name</Label>
                <Input data-testid="resolve-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} className="bg-slate-950/60" /></div>
              <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Classification</Label>
                <Select value={newClass} onValueChange={setNewClass}>
                  <SelectTrigger data-testid="resolve-new-class" className="bg-slate-950/60"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-border bg-[#12161f] text-slate-100">
                    {CLS_OPTIONS.filter((o) => o !== "unidentified_person_reference").map((o) => <SelectItem key={o} value={o}>{CLS[o].label}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
          <Button data-testid="confirm-resolve-button" onClick={save} disabled={saving} className="gap-2 bg-fuchsia-600 text-white hover:bg-fuchsia-500">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Resolve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddCharacterDialog({ projectId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [classification, setClassification] = useState("unresolved");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!name.trim()) return toast.error("Name is required.");
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/characters`, { name: name.trim(), classification });
      toast.success("Character created.");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="border-amber-500/15 bg-[#12161f] text-slate-100 sm:max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Add character</DialogTitle>
          <DialogDescription className="text-slate-400">Manually create a character record.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Name</Label>
            <Input data-testid="add-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-slate-950/60" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-slate-400">Classification</Label>
            <Select value={classification} onValueChange={setClassification}>
              <SelectTrigger data-testid="add-classification" className="bg-slate-950/60"><SelectValue /></SelectTrigger>
              <SelectContent className="border-border bg-[#12161f] text-slate-100">
                {CLS_OPTIONS.map((o) => <SelectItem key={o} value={o}>{CLS[o].label}</SelectItem>)}
              </SelectContent>
            </Select></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-400">Cancel</Button>
          <Button data-testid="save-new-character-button" onClick={save} disabled={saving} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
